import { useEffect, useRef } from 'react';

export default function SpatialCanvas({ width = 320, height = 220 }) {
  const ref = useRef();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = width;
    const H = canvas.height = height;
    const RUST = 'rgba(232,98,26,';
    const MUTED = 'rgba(242,242,240,';

    const points = Array.from({ length: 180 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      z: Math.random(),
      vx: (Math.random() - 0.5) * 0.12,
      vy: (Math.random() - 0.5) * 0.08,
      rust: Math.random() < 0.08,
    }));

    const box = { cx: W*0.5, cy: H*0.48, w:110, h:72, d:48, rx:0.4, ry:0 };

    function project(x, y, z) {
      const cos = Math.cos, sin = Math.sin;
      const rx = box.rx, ry = box.ry;
      const x2 = x*cos(ry) - z*sin(ry);
      const z2 = x*sin(ry) + z*cos(ry);
      const y2 = y*cos(rx) - z2*sin(rx);
      return { sx: box.cx+x2, sy: box.cy+y2 };
    }

    function drawBox(alpha) {
      const hw=box.w/2, hh=box.h/2, hd=box.d/2;
      const verts=[
        [-hw,-hh,-hd],[hw,-hh,-hd],[hw,hh,-hd],[-hw,hh,-hd],
        [-hw,-hh,hd],[hw,-hh,hd],[hw,hh,hd],[-hw,hh,hd],
      ].map(([x,y,z])=>project(x,y,z));
      const edges=[[0,1],[1,2],[2,3],[3,0],[4,5],[5,6],[6,7],[7,4],[0,4],[1,5],[2,6],[3,7]];
      ctx.strokeStyle=`${RUST}${alpha*0.7})`;
      ctx.lineWidth=0.8;
      edges.forEach(([a,b])=>{
        ctx.beginPath();
        ctx.moveTo(verts[a].sx,verts[a].sy);
        ctx.lineTo(verts[b].sx,verts[b].sy);
        ctx.stroke();
      });
      verts.forEach(v=>{
        ctx.beginPath();
        ctx.arc(v.sx,v.sy,1.5,0,Math.PI*2);
        ctx.fillStyle=`${RUST}${alpha})`;
        ctx.fill();
      });
    }

    function drawGrid() {
      ctx.strokeStyle='rgba(42,42,45,0.9)';
      ctx.lineWidth=0.5;
      const baseY=H*0.85;
      const vp={x:W/2,y:H*0.3};
      for(let i=0;i<=8;i++){
        const x=W*0.1+(W*0.8*i/8);
        ctx.beginPath();
        ctx.moveTo(x,baseY);
        ctx.lineTo(vp.x+(x-vp.x)*0.1,vp.y+(baseY-vp.y)*0.1);
        ctx.stroke();
      }
      for(let j=0;j<=4;j++){
        const t=j/4;
        const y=baseY-(baseY-(vp.y+(baseY-vp.y)*0.1))*t;
        const x0=vp.x+(W*0.1-vp.x)*(1-t*0.9);
        const x1=vp.x+(W*0.9-vp.x)*(1-t*0.9);
        ctx.beginPath(); ctx.moveTo(x0,y); ctx.lineTo(x1,y); ctx.stroke();
      }
    }

    let t=0, raf;
    function frame(){
      ctx.clearRect(0,0,W,H);
      t+=0.016;
      box.ry=t*0.18;
      box.rx=0.38+Math.sin(t*0.25)*0.05;
      drawGrid();
      points.forEach(p=>{
        p.x+=p.vx; p.y+=p.vy;
        if(p.x<0)p.x=W; if(p.x>W)p.x=0;
        if(p.y<0)p.y=H; if(p.y>H)p.y=0;
        const size=0.8+p.z*1.4;
        const alpha=0.15+p.z*0.5;
        ctx.beginPath();
        ctx.arc(p.x,p.y,size,0,Math.PI*2);
        ctx.fillStyle=p.rust?`${RUST}${alpha+0.3})`:`${MUTED}${alpha*0.35})`;
        ctx.fill();
      });
      // scan line
      const sy=H*0.2+(H*0.6)*((Math.sin(t*0.4)+1)/2);
      const grad=ctx.createLinearGradient(0,sy-1,0,sy+1);
      grad.addColorStop(0,'transparent');
      grad.addColorStop(0.5,`${RUST}0.35)`);
      grad.addColorStop(1,'transparent');
      ctx.fillStyle=grad; ctx.fillRect(0,sy-1,W,2);
      drawBox(0.85+Math.sin(t*0.5)*0.1);
      // measurement line
      const p1=project(-55,0,0), p2=project(55,0,0);
      ctx.strokeStyle=`${RUST}0.3)`;
      ctx.setLineDash([3,4]); ctx.lineWidth=0.7;
      ctx.beginPath(); ctx.moveTo(p1.sx,p1.sy); ctx.lineTo(p2.sx,p2.sy); ctx.stroke();
      ctx.setLineDash([]);
      [p1,p2].forEach(p=>{
        ctx.beginPath(); ctx.moveTo(p.sx,p.sy-4); ctx.lineTo(p.sx,p.sy+4);
        ctx.strokeStyle=`${RUST}0.45)`; ctx.lineWidth=1; ctx.stroke();
      });
      ctx.fillStyle=`${RUST}0.65)`;
      ctx.font='9px IBM Plex Mono,monospace';
      ctx.textAlign='center';
      ctx.fillText('3.42m',(p1.sx+p2.sx)/2,(p1.sy+p2.sy)/2-6);
      ctx.fillStyle='rgba(107,107,112,0.5)';
      ctx.font='8px IBM Plex Mono,monospace';
      ctx.textAlign='left';
      ctx.fillText('civil3d · spatial scan',10,H-10);
      raf=requestAnimationFrame(frame);
    }
    frame();
    return ()=>cancelAnimationFrame(raf);
  },[width,height]);

  return <canvas ref={ref} style={{ display:'block', borderRadius:8 }} />;
}
